import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import EnhancedAdButton from './EnhancedAdButton';
import EnhancedAdDspSelect from './EnhancedAdDspSelect';
import materialTheme from '../../reactMaterialTheme';
import Mixpanel from '../../shared/Mixpanel';
import http from '../../shared/http';
import {
  useCreativeInput,
  useCurrentUser,
  useNgCreativeService,
  UserProvider
} from '../../hooks';
import { CreativeSubformFields } from '../CreativeSubformFields';
import { AdmField } from './admField';
import Export from '../EnhancedAdRtbExport/Export';
import './EnhancedAdBuilder.scss';

export const EnhancedAdBuilder = props => {
  return (
    <UserProvider>
      <EnhancedAdForm {...props} />
    </UserProvider>
  );
};

const EnhancedAdForm = props => {
  const [creative, setCreative] = useState({});
  const [showExport, setShowExport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useCurrentUser();

  const setAdm = adm => {
    setCreative({ ...creative, adm });
  };

  const creativeInputProps = useCreativeInput(
    ['advertiser', 'headline', 'description', 'name', 'mediaUrl'],
    creative,
    setCreative
  );

  useNgCreativeService(creative, props.CreativeService, props.RenderService);

  useEffect(() => {
    if (props.$state.params && props.$state.params.id) {
      setIsLoading(true);
      http
        .get(`/creative_previews/${props.$state.params.id}.json`)
        .then(({ data }) => {
          setCreative({
            id: data.id,
            action: data.action,
            adm: data.adm,
            name: data.name,
            advertiser: data.advertiser,
            headline: data.headline,
            description: data.description,
            dspId: data.dsp_id || -1,
            dsp: data.dsp,
            rtb_pixel: data.rtb_pixel,
            mediaUrl: data.media_url,
            assetSwapUrl: data.asset_swap_url
          });
          setIsLoading(false);
          setShowExport(true);
        })
        .catch(() => {
          setIsLoading(false);
        });

      Mixpanel.track('Edit Enhanced Ad');
    } else {
      const creative = {
        action: 'banner',
        adm: '',
        name: '',
        advertiser: 'Sponsor',
        headline: 'Click here for more information',
        description: "Don't miss this content from our sponsor",
        dspId: -1
      };
      setCreative(creative);
      Mixpanel.track('New Enhanced Ad');
    }
  }, []);

  // this logic is the validity for this form only and not creatives in general
  const creativeValid = () => {
    return (
      creative.adm &&
      creative.name &&
      creative.advertiser &&
      creative.headline &&
      creative.description
    );
  };

  const flashSaveError = () => {
    props.FlashService.push(
      'error',
      'Creative must contain markup, name, sponsor, headline, and a description'
    );
  };

  const generatePixel = () => {
    return http
      .post('/rtb_pixels', {
        preview_id: props.CreativeService.getCreative().id
      })
      .then(({ data }) => {
        setCreative(oldCreative => ({
          ...oldCreative,
          rtb_pixel: { pixel_key: data.pixel_key }
        }));
      });
  };

  const handleSaveClick = () => {
    if (!creativeValid()) {
      flashSaveError();
      return;
    }

    props.SaveCreativeService.save(props.CreativeService.getCreative())
      .then(() => {
        setCreative(oldCreative => ({
          ...oldCreative,
          id: props.CreativeService.getCreative().id
        }));
        setShowExport(true);

        if (creative.dsp.rtb_version === '2.3' && !creative.rtb_pixel) {
          return generatePixel();
        }
      })
      .catch(() => {
        props.FlashService.push(
          'error',
          'Something went wrong. Please try again.'
        );
      });

    if (creative.dsp.rtb_version === '2.3') {
      Mixpanel.track('Generate pixel and save new Enhanced Ad');
    } else {
      Mixpanel.track('Save existing Enhanced Ad');
    }
  };

  const handleBackClick = () => {
    let path;
    if (currentUser.isLoggedIn()) {
      path = 'creativePreview.enhanced.index';
    } else {
      path = 'creativePreview.home';
    }

    props.$state.go(path, {}, { reload: true });
  };

  const handleDspChange = dsp => {
    setCreative(prevCreative => ({ ...prevCreative, dspId: dsp.id, dsp: dsp }));
  };

  if (Object.keys(creative).length === 0 && !isLoading) {
    return props.$state.params && props.$state.params.id ? (
      <h4>{`No creative found for ID "${props.$state.params.id}"`}</h4>
    ) : (
      <h4>No creative found</h4>
    );
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <MuiThemeProvider theme={materialTheme}>
      <div
        role="link"
        tabIndex={0}
        className="clickable text-muted mb2 ml3"
        onClick={handleBackClick}
        onKeyDown={handleBackClick}
      >
        &lt; Back to list
      </div>
      <form className="enhanced-ad_form" autoComplete="off">
        {creative.id && <span>Banner ID: {creative.id}</span>}
        {currentUser && (
          <AdmField
            adm={creative.adm}
            setAdm={setAdm}
            currentUser={currentUser}
          />
        )}
        <div className="enhanced-ad_subform">
          <CreativeSubformFields
            headlineProps={creativeInputProps.headlineProps}
            descriptionProps={creativeInputProps.descriptionProps}
            nameProps={creativeInputProps.nameProps}
            advertiserProps={creativeInputProps.advertiserProps}
            mediaUrlProps={creativeInputProps.mediaUrlProps}
            nameLabel="Name your enhanced ad"
          />
          <EnhancedAdDspSelect
            onDspChange={handleDspChange}
            activeDspId={creative.dspId}
          />

          {creative.dsp && (
            <EnhancedAdButton
              creative={creative}
              creativeValid={creativeValid}
              onClick={handleSaveClick}
            />
          )}
        </div>
        {showExport && (
          <Export AdMarkupService={props.AdMarkupService} creative={creative} />
        )}
      </form>
    </MuiThemeProvider>
  );
};

EnhancedAdBuilder.propTypes = {
  $state: PropTypes.shape({
    params: PropTypes.object,
    go: PropTypes.func
  }).isRequired,
  AdMarkupService: PropTypes.shape({
    generate: PropTypes.func
  }).isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func,
    setCreative: PropTypes.func,
    getCreative: PropTypes.func
  }).isRequired,
  FlashService: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  RenderService: PropTypes.shape({
    renderDemo: PropTypes.func
  }).isRequired,
  SaveCreativeService: PropTypes.shape({
    save: PropTypes.func
  }).isRequired
};
EnhancedAdForm.propTypes = EnhancedAdBuilder.propTypes;
